/* Context for the Chatbot component */
import React, {createContext, useContext, useEffect, useState} from 'react';
import {INTRO_MESSAGE, isAIMessage, isNotDefaultIntroMessage} from "@components/Chatbot/utils";
import {useSelector} from "react-redux";
import {useAuth0} from "@auth0/auth0-react";
import { isStaffEmail } from "@utils/user_utils";
import {TrainingAPI} from "@api/TrainingAPI";

let trainingAPI = new TrainingAPI();

export const ChatbotContext = createContext({
    messages: [],
    appendNewMessage: () => {},
    conversationId: null,
    setConversationId: () => {},
    conversations: [],
    setConversations: () => {},
    streamingUuid: null,
    setStreamingUuid: () => {},
    isTyping: false,
    setIsTyping: () => {},
    intermediateMessages: [],
    setIntermediateMessages: () => {},
    followUpMessages: [],
    setFollowUpMessages: () => {},
    followUpLoading: false,
    setFollowUpLoading: () => {},
    actionMetadata: {},
    selectedMessage: null,
    setSelectedMessage: () => {},
    isMessageSelected: (target) => false,
    drawerOpen: false,
    setDrawerOpen: () => {},
});
export const useChatbotContext = () => useContext(ChatbotContext);

export const ChatbotProvider = ({ children }) => {
    const [messages, setMessages] = useState([INTRO_MESSAGE]);
    const [conversationId, setConversationId] = useState(null);
    const [conversations, setConversations] = useState([]);
    const [streamingUuid, setStreamingUuid] = useState(crypto.randomUUID());
    const [isTyping, setIsTyping] = useState(false);
    const [intermediateMessages, setIntermediateMessages] = useState([]);
    const [followUpMessages, setFollowUpMessages] = useState([]);
    const [followUpLoading, setFollowUpLoading] = useState(false);
    const [actionMetadata, setActionMetadata] = useState({});
    const [selectedMessage, setSelectedMessage] = useState(null);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const { user} = useAuth0();
    const {
        info : {dvai_enabled},
    } = useSelector(({team}) => team);

    const isStaff = isStaffEmail(user?.email);

    const isChatbotEnabled = dvai_enabled || isStaff;

    const getActionMetadata = () => {
        try {
            trainingAPI.getChatbotActionMetadata().then(data => {
                setActionMetadata(data);
            })
        } catch (error) {
            console.error("Error fetching chatbot action metadata:", error);
        }
    }

    useEffect(() => {
        getActionMetadata();
    }, [])

    const appendNewMessage = (newMessage) => {
        setMessages(prevMessages => {
            return [...prevMessages, newMessage]
        });
    }

    const setConversation = (conversation) => {
        setConversationId(conversation.id);
        setMessages(conversation.messages);
        setStreamingUuid(crypto.randomUUID());
        setFollowUpMessages([]);
        setIntermediateMessages([]);

        // resolve message drawer items
        setSelectedMessage(null);
        setDrawerOpen(false);
    }

    const resetConversation = () => {
        setConversationId(null);
        setMessages([INTRO_MESSAGE]);
        setStreamingUuid(crypto.randomUUID());
        setFollowUpMessages([]);
        setIntermediateMessages([]);
    }

    const handleSelectMessage = (message) => {
        if (isMessageSelected(message)) {
            setSelectedMessage(null);
            setDrawerOpen(false);
        } else if (!(isAIMessage(message) && isNotDefaultIntroMessage(message))) {
            // Only non-intro AI messages can be selected

        }
        else {
            setSelectedMessage(message);
            setDrawerOpen(true);
        }
    }

    const isMessageSelected = (target) => {
        if (!selectedMessage) return false;
        return selectedMessage.id === target.id;
    }

    return (
        <ChatbotContext.Provider value={{
            isChatbotEnabled,
            messages,
            setMessages,
            appendNewMessage,
            conversationId,
            setConversationId,
            conversations,
            setConversations,
            streamingUuid,
            setStreamingUuid,
            isTyping,
            setIsTyping,
            intermediateMessages,
            setIntermediateMessages,
            followUpMessages,
            setFollowUpMessages,
            followUpLoading,
            setFollowUpLoading,
            setConversation,
            resetConversation,
            actionMetadata,
            selectedMessage,
            setSelectedMessage: handleSelectMessage,
            isMessageSelected,
            drawerOpen,
            setDrawerOpen,
        }}>
            {children}
        </ChatbotContext.Provider>
    );
}
